import React, { useState, useEffect, useMemo } from 'react';
import api from '../api';
import { PageHeader, Card, Button, LoadingSpinner } from '../components/UI';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import * as I from 'lucide-react';

const TdsPendingBatchInvoicesPage = ({ batch, onBack }) => {
  const { isAuditor } = useAuth(); // AUDITOR is read-only: hide the remove-from-batch action
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tdsInterestData, setTdsInterestData] = useState({});
  const [loadingTdsInterest, setLoadingTdsInterest] = useState({});

  const [searchTerm, setSearchTerm] = useState('');
  const [vendorMap, setVendorMap] = useState({});
  const [deleteDialog, setDeleteDialog] = useState({ open: false, invoice: null, isLast: false });
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    const fetchInvoices = async () => {
      if (!batch || !batch.id) {
        setError('Batch id is missing; unable to load invoices.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const resp = await api.get(`/invoices/by-tds-batch/${batch.id}`);
        const data = Array.isArray(resp.data) ? resp.data : [];
        setInvoices(data);
        if (!data.length) setError('No invoices found for this batch.');
      } catch (err) {
        console.error('Error loading invoices for batch', err);
        const msg = err?.response?.data?.message || 'Failed to load invoice details for this batch.';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoices();
    // After fetching invoices, fetch TDS interest for each
    if (invoices && invoices.length > 0) {
      console.log('Invoices loaded:', invoices); // Debug log
      // Fetch TDS interest for each invoice
      const fetchAllTdsInterests = async () => {
        const results = await Promise.all(
          invoices.map(async (inv) => {
            if (!inv.id) return null;
            try {
              const response = await api.get(`/internal/invoices/${inv.id}/tds-interest-current`);
              console.log('TDS Interest response:', response.data); // Debug log
              return {
                invoiceId: inv.id,
                data: {
                  months: response.data.months || 0,
                  interest: response.data.interest || 0,
                  invoiceId: response.data.invoiceId
                }
              };
            } catch (error) {
              console.error(`Error fetching TDS interest for invoice ${inv.id}:`, error);
              return {
                invoiceId: inv.id,
                data: { months: 0, interest: 0 }
              };
            }
          })
        );

        // Update state with all the fetched data
        const newTdsData = {};
        results.forEach(result => {
          if (result) {
            newTdsData[result.invoiceId] = result.data;
          }
        });
        setTdsInterestData(prev => ({ ...prev, ...newTdsData }));
      };

      fetchAllTdsInterests();
    }
  }, [batch]);

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const missingVendorIds = [
          ...new Set(
            invoices
              .map((inv) => inv.vendorId)
              .filter((id) => id != null && !vendorMap[id])
          ),
        ];
        if (!missingVendorIds.length) return;

        const responses = await Promise.all(
          missingVendorIds.map((id) =>
            api
              .get(`/vendors/${id}`)
              .then((res) => ({ id, name: res.data?.name || `Vendor ${id}` }))
              .catch(() => ({ id, name: `Vendor ${id}` })),
          ),
        );

        const newMap = {};
        responses.forEach(({ id, name }) => {
          newMap[id] = name;
        });
        if (Object.keys(newMap).length) {
          setVendorMap((prev) => ({ ...prev, ...newMap }));
        }
      } catch (e) {
        console.error('Failed to fetch vendors for batch invoices', e);
      }
    };

    if (invoices.length) {
      fetchVendors();
    }
  }, [invoices, vendorMap]);

  const formatCurrency = (amount) => {
    if (amount == null) return '-';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(Number(amount));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const filteredInvoices = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return invoices.filter((inv) => {
      if (!term) return true;

      const vendorNameResolved =
        inv.vendorName ||
        (inv.vendor && inv.vendor.name) ||
        (inv.vendorId != null ? vendorMap[inv.vendorId] : '') ||
        '';

      const vendorName = vendorNameResolved.toLowerCase();
      const invoiceNumber = (inv.invoiceNumber || '').toString().toLowerCase();

      return (
        (invoiceNumber && invoiceNumber.includes(term)) ||
        (vendorName && vendorName.includes(term))
      );
    });
  }, [invoices, searchTerm, vendorMap]);

  const totalInvoiceCount = invoices.length;

  // Calculate total TDS amount
  const totalTdsAmount = useMemo(() => {
    return invoices.reduce((sum, inv) => {
      const val = inv.tdsAmount != null ? Number(inv.tdsAmount) : 0;
      return sum + (Number.isNaN(val) ? 0 : val);
    }, 0);
  }, [invoices]);

  // Calculate total TDS interest (using real-time data with fallback)
  const totalTdsInterest = useMemo(() => {
    let total = 0;
    
    invoices.forEach(inv => {
      // First try to get real-time data
      if (tdsInterestData[inv.id] && tdsInterestData[inv.id].interest !== undefined) {
        total += Number(tdsInterestData[inv.id].interest) || 0;
      } else {
        // Fallback to stored invoice data
        if (inv.tdsLatePaymentInterest != null) {
          total += Number(inv.tdsLatePaymentInterest) || 0;
        }
      }
    });
    
    return total;
  }, [invoices, tdsInterestData]);
  
  // Calculate total TDS payable (using real-time data)
  const totalTdsPayable = totalTdsAmount + totalTdsInterest;

  const openRemoveDialog = (inv) => {
    if (!batch || !batch.id || !inv || !inv.id) return;
    const isLast = invoices.length === 1;
    setDeleteDialog({ open: true, invoice: inv, isLast });
  };

  const handleConfirmRemove = async () => {
    if (!batch || !batch.id || !deleteDialog.invoice || !deleteDialog.invoice.id) return;
    const inv = deleteDialog.invoice;

    try {
      setRemoving(true);
      const resp = await api.delete(`/tds/batches/${batch.id}/invoices/${inv.id}`);
      const updatedBatch = resp?.data;

      setInvoices((prev) => prev.filter((x) => x.id !== inv.id));

      const remainingCount = updatedBatch && typeof updatedBatch.invoiceCount === 'number'
        ? updatedBatch.invoiceCount
        : invoices.length - 1;

      if (remainingCount <= 0) {
        toast.success('Invoice removed and TDS batch deleted');
        setDeleteDialog({ open: false, invoice: null, isLast: false });
        setRemoving(false);
        if (onBack) {
          onBack();
        }
        return;
      }

      toast.success('Invoice removed from batch');
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to remove invoice from batch.';
      toast.error(msg);
    } finally {
      setRemoving(false);
      setDeleteDialog({ open: false, invoice: null, isLast: false });
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 bg-gray-50 min-h-screen">
      <PageHeader
        title="Pending Batch Invoices"
        subtitle={batch ? `Batch: ${batch.batchNumber || batch.id}` : 'No batch selected'}
        actions={[
          <Button key="back" variant="outline" onClick={onBack}>
            <I.ArrowLeft className="w-4 h-4 mr-2" />
            Back to Pending Batches
          </Button>,
        ]}
      />

      <Card>
        {/* Batch Summary */}
        {batch && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <h4 className="text-sm font-semibold text-blue-800 mb-2">Batch Summary</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div>
                <span className="text-gray-600">Total TDS:</span>
                <div className="font-semibold text-gray-800">{formatCurrency(totalTdsAmount)}</div>
              </div>
              <div>
                <span className="text-gray-600">Total Interest:</span>
                <div className="font-semibold text-amber-700">
                  {Object.keys(loadingTdsInterest).length > 0 ? (
                    <div className="flex items-center gap-1">
                      <I.Loader2 className="w-3 h-3 animate-spin" />
                      Loading...
                    </div>
                  ) : (
                    formatCurrency(totalTdsInterest)
                  )}
                </div>
              </div>
              <div>
                <span className="text-gray-600">Final Amount:</span>
                <div className="font-semibold text-blue-600">{formatCurrency(totalTdsPayable)}</div>
              </div>
              <div>
                <span className="text-gray-600">Invoices:</span>
                <div className="font-semibold text-gray-800">{totalInvoiceCount}</div>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex flex-col gap-3 mb-3">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-lg font-medium">Invoices in Batch</h3>
            <div className="flex items-center gap-2 text-xs">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by invoice # or vendor..."
                className="border border-gray-300 rounded px-2 py-1 text-xs w-48"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="md" />
            <span className="ml-3">Loading invoices...</span>
          </div>
        ) : error ? (
          <div className="text-sm text-red-600">{error}</div>
        ) : filteredInvoices.length === 0 ? (
          <div className="text-sm text-gray-500">No invoices found for this batch.</div>
        ) : (
          <div className="overflow-auto text-xs">
            <table className="min-w-full divide-y divide-gray-200 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    <div className="sticky left-0 bg-gray-50 z-10 pl-2 pr-4 -ml-2">Invoice #</div>
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date Recvd</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Invoice Date</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vendor Name</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Tax</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    TDS Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    TDS Deducted Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Default Months
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-amber-700 uppercase">TDS Interest</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-blue-700 uppercase">Final TDS Payable</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Invoice Paid</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInvoices.map((inv) => {
                  const vendorName =
                    inv.vendorName ||
                    (inv.vendor && inv.vendor.name) ||
                    (inv.vendorId != null ? vendorMap[inv.vendorId] : `Vendor ${inv.vendorId || ''}`);
                  
                  const tdsAmt = inv.tdsAmount != null ? Number(inv.tdsAmount) : 0;
                  const lateMonths = inv.lateMonths != null ? Number(inv.lateMonths) : 0;
                  const tdsInterest = tdsInterestData[inv.id]?.interest !== undefined 
                    ? Number(tdsInterestData[inv.id].interest) 
                    : (inv.tdsLatePaymentInterest != null ? Number(inv.tdsLatePaymentInterest) : 0);

                  const finalTdsPayable = tdsAmt + tdsInterest;
                  const invoicePayable = inv.invoicePayable != null ? inv.invoicePayable : 0;

                  return (
                    <tr key={inv.id || inv.invoiceNumber} className="text-xs">
                      <td className="px-4 py-1 text-gray-700">
                        <div className="sticky left-0 bg-white z-10 pl-2 pr-4 -ml-2">
                          {inv.invoiceNumber || inv.id || 'N/A'}
                        </div>
                      </td>
                      <td className="px-4 py-1 text-gray-700">
                        {inv.dateReceived || inv.receivedDateTime ? formatDate(inv.dateReceived || inv.receivedDateTime) : 'N/A'}
                      </td>
                      <td className="px-4 py-1 text-gray-700">
                        {inv.invoiceDate ? formatDate(inv.invoiceDate) : 'N/A'}
                      </td>
                      <td className="px-4 py-1 text-gray-700">
                        <div className="min-w-[150px] max-w-[250px] break-words whitespace-normal">{vendorName}</div>
                      </td>
                      <td className="px-4 py-1 text-right text-gray-700">
                        {inv.tax != null ? formatCurrency(inv.tax) : 'N/A'}
                      </td>
                      <td className="px-4 py-1 text-right text-gray-700">
                        {inv.totalAmount != null ? formatCurrency(inv.totalAmount) : 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(inv.tdsAmount)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(inv.invoicePaidDate) || 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 group relative">
                        {loadingTdsInterest[inv.id] ? (
                          <I.Loader2 className="w-3 h-3 animate-spin inline-block" />
                        ) : (
                          <div className="inline-flex items-center">
                            <span className="font-medium">
                              {tdsInterestData[inv.id]?.months !== undefined ? 
                                tdsInterestData[inv.id].months : 
                                (inv.tdsDefaultMonths || lateMonths || 0)}
                            </span>
                            {tdsInterestData[inv.id]?.months > 0 && (
                              <I.Info className="w-3 h-3 ml-1 text-gray-400 group-hover:text-blue-500" />
                            )}
                          </div>
                        )}
                        {tdsInterestData[inv.id]?.months > 0 && (
                          <div className="absolute z-10 hidden group-hover:block w-64 p-2 mt-1 -ml-32 text-xs text-gray-600 bg-white border border-gray-200 rounded shadow-lg">
                            <p>Default Months: <span className="font-semibold">
                              {tdsInterestData[inv.id]?.months || inv.tdsDefaultMonths || 0}
                            </span></p>
                            <p>Interest: <span className="font-semibold">
                              {tdsInterestData[inv.id]?.interest ? formatCurrency(tdsInterestData[inv.id].interest) : formatCurrency(0)}
                            </span></p>
                            <p className="text-gray-400 text-xs mt-1">Calculated as of {new Date().toLocaleDateString()}</p>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-1 text-right text-amber-700">
                        {loadingTdsInterest[inv.id] ? (
                          <I.Loader2 className="w-3 h-3 animate-spin inline-block" />
                        ) : (
                          tdsInterestData[inv.id]?.interest !== undefined 
                            ? formatCurrency(tdsInterestData[inv.id].interest) 
                            : (!isNaN(tdsInterest) ? formatCurrency(tdsInterest) : 'N/A')
                        )}
                      </td>
                      <td className="px-4 py-1 text-right text-blue-700 font-medium">
                        {!isNaN(finalTdsPayable) ? formatCurrency(finalTdsPayable) : 'N/A'}
                      </td>
                      <td className="px-4 py-1 text-right text-gray-700">
                        {formatCurrency(invoicePayable)}
                      </td>
                      <td className="px-4 py-1 text-right text-gray-700">
                        {!isAuditor && (
                          <button
                            type="button"
                            className="inline-flex items-center rounded border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700 hover:bg-red-100"
                            onClick={() => openRemoveDialog(inv)}
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {deleteDialog.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md text-xs sm:text-sm">
            <div className="px-3 py-2 border-b flex items-center justify-between">
              <h3 className="text-base font-semibold">Confirm Removal</h3>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => !removing && setDeleteDialog({ open: false, invoice: null, isLast: false })}
                disabled={removing}
              >
                ×
              </button>
            </div>
            <div className="px-3 py-3 space-y-2">
              <p className="text-gray-700">
                {deleteDialog.isLast ? (
                  <>
                    You are removing the <span className="font-semibold">last invoice</span> from this TDS batch.
                    This will <span className="font-semibold text-red-600">delete the entire batch</span>:
                    {' '}
                    <span className="font-mono">{batch?.batchNumber || batch?.id}</span>.
                  </>
                ) : (
                  <>Remove this invoice from the TDS batch?</>
                )}
              </p>
              {deleteDialog.invoice && (
                <div className="mt-2 p-2 border border-gray-200 rounded bg-gray-50 text-xs text-gray-800">
                  <div className="font-semibold mb-1">Invoice Details</div>
                  <div>Invoice #: <span className="font-medium">{deleteDialog.invoice.invoiceNumber || deleteDialog.invoice.id}</span></div>
                  <div>Vendor: <span className="font-medium">{
                    deleteDialog.invoice.vendorName ||
                    (deleteDialog.invoice.vendor && deleteDialog.invoice.vendor.name) ||
                    (deleteDialog.invoice.vendorId != null ? vendorMap[deleteDialog.invoice.vendorId] : '')
                  }</span></div>
                  <div>TDS Amount: <span className="font-medium">{formatCurrency(deleteDialog.invoice.tdsAmount || 0)}</span></div>
                  <div>TDS Deducted Date: <span className="font-medium">{deleteDialog.invoice.invoicePaidDate || deleteDialog.invoice.paymentDate || deleteDialog.invoice.datePaid ? formatDate(deleteDialog.invoice.invoicePaidDate || deleteDialog.invoice.paymentDate || deleteDialog.invoice.datePaid) : 'N/A'}</span></div>
                  <div>Late Months: <span className="font-medium">{deleteDialog.invoice.lateMonths || 0}</span></div>
                  <div>TDS Interest (1.5% per month): <span className="font-medium text-amber-700">{formatCurrency(deleteDialog.invoice.tdsLatePaymentInterest || 0)}</span></div>
                  <div className="font-medium text-blue-700">Final TDS Payable: <span className="font-semibold">{formatCurrency((deleteDialog.invoice.tdsAmount || 0) + (deleteDialog.invoice.tdsLatePaymentInterest || 0))}</span></div>
                </div>
              )}
            </div>
            <div className="px-3 py-3 border-t flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => !removing && setDeleteDialog({ open: false, invoice: null, isLast: false })}
                disabled={removing}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmRemove}
                disabled={removing}
              >
                {removing ? 'Removing...' : (deleteDialog.isLast ? 'Remove & Delete Batch' : 'Remove')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TdsPendingBatchInvoicesPage;
